import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { PageHero } from '../Common/PageLayout';
import useEntries from '../Common/useEntries';
import { directorateName } from '../../subdomains/portal';
import './OrganizationChart.css';

const directorateGroups = [
    {
        code: 'AA',
        slug: 'autoridade-aduaneira',
        name: 'Customs',
        aliases: ['Customs', 'Customs Authority'],
        units: [],
    },
    {
        code: 'AT',
        slug: 'autoridade-tributaria',
        name: 'Tax Authority',
        aliases: ['Tax Authority', 'Autoridade Tributaria'],
        units: [],
    },
    {
        code: 'DGGMRE',
        slug: 'direcao-geral-de-gestao-e-mobilizacao-dos-ajudos-externa',
        name: 'Management and Mobilisation of External Resources',
        aliases: ['Management and Mobilisation of External Resources', 'Management and Mobilization of External Resources'],
        units: [
            ['DGNAMEA', 'Management of External Aid'],
            ['DNPPP', 'Public-Private Partnerships'],
            ['DNPME', 'Policy and Loan Mobilisation'],
        ],
    },
    {
        code: 'DGPO',
        slug: 'diresaun-geral-planeamentu-e-orsamentu',
        name: 'Budget Planning',
        aliases: ['Budget Planning'],
        units: [
            ['DNPE', 'Economic Policy'],
            ['DNO', 'Budget'],
            ['DNP', 'Planning'],
            ['DNPO', 'Planning, Budget and Performance Evaluation'],
        ],
    },
    {
        code: 'DGGPE',
        slug: 'direcao-geral-de-gestao-do-patrimonio-do-estado-dggpe',
        name: 'State Asset Management',
        aliases: ['State Asset Management', 'Directorate-General for State and Finance'],
        units: [
            ['DNABM', 'Disposal of Movable Assets'],
            ['DNGVE', 'State Vehicle Management'],
            ['DNGPIPE', 'State Inventory and Asset Policy'],
        ],
    },
    {
        code: 'DGT',
        slug: 'diresaun-jeral-tezouru',
        name: 'Treasury',
        aliases: ['Treasury'],
        units: [
            ['DNCNC', 'Accounting and Accounts Standardisation'],
            ['DNMF', 'Financial Decentralisation and Monitoring'],
            ['DNTDP', 'Treasury Public Debt Management'],
            ['DNPADGE', 'Payments Administration'],
        ],
    },
    {
        code: 'DGSC',
        slug: 'direcao-geral-de-servicos-corporativos-dgsc',
        name: 'Corporate Services',
        aliases: ['Corporate Services', 'Corporate Services Logistics Maintenance Project Management'],
        units: [
            ['DNAGCA', 'Procurement and Contract Management'],
            ['DNA', 'Administration, Contracts and Archives'],
            ['DNGFAEM', 'Director of Finance'],
            ['DNRH', 'Human Resources'],
            ['DNLOGP', 'Logistics'],
        ],
    },
];

const specialRows = [
    {
        title: 'Independent institutions',
        nodes: [
            ['RTA', 'Board of Tax Appeals', []],
            ['INETL', 'National Institute of Statistics', ['Statistics Law'], 'instituto-nacional-de-estatistica-timor-leste-i-p'],
        ],
    },
    {
        title: 'Ministerial offices and advisory bodies',
        nodes: [
            ['UAJ', 'Legal Office', [], 'gabineti-apoiu-juridiku'],
            ['UCRP', 'Communications', [], 'unidade-de-comunicacao-e-relacoes-publicas-ucrp'],
            ['UIG', 'Inspector General', ['Office of Inspection and Audit'], 'gabineti-inspesaun-jeral'],
            ['UMAD', 'Monitoring and Evaluation', [], 'unidade-de-monitorizacao-e-avaliacao-do-desempenho-umad'],
            ['CCFM', 'Consultative Council', []],
            ['UPGFP', 'Public Financial Management', [], 'gabinete-de-politica-de-reforma-capacitacao-gestao-financas-publica'],
            ['CCGFP', 'Capacity Building', [], 'gabinete-de-politica-de-reforma-capacitacao-gestao-financas-publica'],
            ['USIIGF', 'Information Systems (IFMIS)', ['Integrated Financial Management Information System Unit'], 'gabinete-sistema-integradu-informasaun-gestaun-finanseira'],
            ['UPRGFP', 'Petroleum Fund Management', ['Petroleum Fund Directorate'], 'gabineti-politika-no-gestaun-fundu-minarai'],
        ],
    },
];

const normalize = (value) => (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');

function findDirectorateEntry(entries, candidates, slug) {
    const bySlug = slug && entries.find((item) => item.fields?.slug === slug);
    if (bySlug) return bySlug;
    const normalizedCandidates = candidates.map(normalize);
    return entries.find((item) => {
        const searchableFields = [directorateName(item.fields), item.fields?.slug].map(normalize);
        return searchableFields.some((field) =>
            field && normalizedCandidates.some((candidate) => candidate === field || (candidate.length >= 7 && field.includes(candidate)))
        );
    });
}

/**
 * Page a chart node opens. Strapi names directorates in Tetum while the chart
 * uses English titles, so each node carries its CMS slug. Until the list has
 * loaded the slug is trusted as-is; once loaded, a slug that no longer exists
 * falls back to matching by name, then to `fallbackTo`.
 */
function nodeDestination(entries, loading, candidates, slug, fallbackTo) {
    if (slug && loading) return `/directoratedetails/${slug}`;
    const entry = findDirectorateEntry(entries, candidates, slug);
    return entry?.fields?.slug ? `/directoratedetails/${entry.fields.slug}` : fallbackTo;
}

function NodeLink({ code, title, aliases = [], slug, entries, loading, prominent = false, fallbackTo = '/directorates' }) {
    const destination = nodeDestination(entries, loading, [code, title, ...aliases], slug, fallbackTo);
    const opensDirectory = destination === '/directorates';

    return (
        <Link className={`org-chart__node${prominent ? ' org-chart__node--prominent' : ''}`} to={destination}>
            <span className="org-chart__node-code">{code}</span>
            <span className="org-chart__node-title">{title}</span>
            <span className="org-chart__node-action">{opensDirectory ? 'Browse directory' : 'View details'}</span>
        </Link>
    );
}

export default function OrganizationChartPage({ embedded = false }) {
    const { items = [], loading } = useEntries({
        content_type: 'directorates',
        order: 'fields.directorateName',
        limit: 200,
    });

    return (
        <>
            {!embedded && (
                <Helmet>
                    <title>Organizational Chart | Ministry of Finance, Timor-Leste</title>
                    <meta
                        name="description"
                        content="Explore the Ministry of Finance organizational structure and navigate to directorate information."
                    />
                </Helmet>
            )}

            {!embedded && (
                <PageHero
                    eyebrow="About the Ministry"
                    title="Organizational structure"
                    description="Explore the Ministry's leadership, offices and directorates. Select a unit to open its information."
                    breadcrumbs={[
                        { label: 'About the Ministry', to: '/profile' },
                        { label: 'Organizational structure' },
                    ]}
                />
            )}

            <div className="org-chart-page">
                <div className="org-chart__toolbar">
                    <div>
                        <span className="org-chart__eyebrow">Ministry of Finance</span>
                        <h2>Leadership and directorates</h2>
                    </div>
                    <Link className="org-chart__directory-link" to="/directorates">
                        View all Directorates <span aria-hidden="true">&rarr;</span>
                    </Link>
                </div>

                {loading && <p className="org-chart__status" role="status">Loading directorate links...</p>}

                <div className="org-chart" aria-label="Ministry of Finance organizational chart">
                    <div className="org-chart__leadership">
                        <Link className="org-chart__minister" to="/profile">
                            <span className="org-chart__role">Minister</span>
                            <strong>Santina José Rodrigues F. Viegas Cardoso</strong>
                        </Link>
                        <Link className="org-chart__vice-minister" to="/profile">
                            <span className="org-chart__role">Vice Minister</span>
                            <strong>Regina de Jesus de Sousa</strong>
                        </Link>
                    </div>

                    <section className="org-chart__tier" aria-label="Institutions and ministerial offices">
                        {specialRows.map((row) => (
                            <div className="org-chart__group" key={row.title}>
                                <h3>{row.title}</h3>
                                <div className="org-chart__nodes">
                                    {row.nodes.map(([code, title, aliases, slug]) => (
                                        <NodeLink
                                            key={code}
                                            code={code}
                                            title={title}
                                            aliases={aliases}
                                            slug={slug}
                                            entries={items}
                                            loading={loading}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}
                    </section>

                    <section className="org-chart__tier org-chart__tier--directorates" aria-label="General directorates and national directorates">
                        <div className="org-chart__tier-heading">
                            <span className="org-chart__eyebrow">Operational structure</span>
                            <h3>General directorates</h3>
                        </div>
                        <div className="org-chart__directorate-grid">
                            {directorateGroups.map((group) => {
                                const parentPage = nodeDestination(
                                    items,
                                    loading,
                                    [group.code, group.name, ...group.aliases],
                                    group.slug,
                                    '/directorates'
                                );

                                return (
                                    <div className="org-chart__directorate-group" key={group.code}>
                                        <NodeLink
                                            code={group.code}
                                            title={group.name}
                                            aliases={group.aliases}
                                            slug={group.slug}
                                            entries={items}
                                            loading={loading}
                                            prominent
                                        />
                                        {group.units.length > 0 && (
                                            <div className="org-chart__units">
                                                {group.units.map(([code, title]) => (
                                                    <NodeLink
                                                        key={code}
                                                        code={code}
                                                        title={title}
                                                        entries={items}
                                                        loading={loading}
                                                        fallbackTo={parentPage}
                                                    />
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                </div>

                {!loading && items.length === 0 && (
                    <p className="org-chart__note">
                        Directorate details are currently unavailable. You can still browse the full directory.
                    </p>
                )}
            </div>
        </>
    );
}